/**
 * Unified color themes for web + mobile, assembled from the canonical scales
 * in ./baseColors.
 *
 * Structure of every theme object (per mode):
 *  - shared groups   primary / semantic / neutral / backgrounds / borders /
 *                    text / shadows — ONE set of values for both apps.
 *                    Formulas follow web's colorTheme.js (mobile's dark-mode
 *                    copies of these groups had inverted-ramp bugs: near-white
 *                    dark backgrounds, black-on-black rgba overlays).
 *  - shared blocks   electric + home_menu_colors (from mobile),
 *                    white/black/gray/green/red/yellow spreads, hero_background,
 *                    navbar_test, buttons, background, ui, features, dashboard
 *                    (from web). Harmless to the app that doesn't use them.
 * The per-platform legacy alias layer (flat THEME_* / COLOR_* keys) was fully
 * removed once all consumption migrated to the shared groups; `platform` now only
 * selects the mobile-dark surface overrides.
 *
 * Golden parity tests in __tests__/theme.test.js diff these objects against
 * BOTH apps' current source files; every intentional divergence is pinned
 * there, anything else failing is a regression.
 */

import { lighten, darken, rgbaString } from './colorUtils';
import { resolveBase } from './palette';
import { ACTIVE_META } from './baseScales';
import { MENU_CHIP_COLORS } from './menuChips';

export const ELECTRIC_COLORS = {
  blue: "#0088FF",
  green: "#00FF88",
  purple: "#8800FF",
  pink: "#FF0088",
  lime: "#88FF00",
  orange: "#FF8800"
};

// ---------------------------------------------------------------------------
// Theme builder — assembles all four theme objects from the ACTIVE brand's baked
// scales (theme/generated/activeBrandScales.js). Brand identity flows entirely
// through those baked families (L/D) plus ACTIVE_META (the derived hero styling);
// there are no per-brand palette objects or literal token overrides.
// ---------------------------------------------------------------------------
// `input` (optional) injects a specific brand's scales + meta — used by the golden tests to build
// any brand's assembled theme from its baked families (configs → familiesFor → buildThemes), without
// re-baking the runtime file. Omitted at runtime → the ACTIVE brand (resolveBase() + ACTIVE_META).
export function buildThemes(input) {
  const L = input ? input.L : resolveBase().L;
  const D = input ? input.D : resolveBase().D;
  const meta = (input && input.meta) || ACTIVE_META;

  // Welcome-hero banner — DERIVED from the active brand (never a hand-written override).
  //  style 'brand'   : a brand-gradient banner; name reads white unless the brand pins hero.accent.
  //  style 'surface' : the banner is the brand's dark surface (slate) with a deep-brand corner glow,
  //                    and the name-highlight is the brand's on-dark tone (L.blue[400]) unless pinned.
  // The banner is always dark for 'surface', so both app modes share those (L-derived) values; for
  // 'brand' the light/dark banner tracks the light/dark primary scale as before.
  const heroStyle = (meta && meta.heroStyle) || 'brand';
  const heroAccentCfg = meta ? meta.heroAccent : null;
  // Dark-mode 'brand' heroes use the deeper end of the dark scale (blue[100]/
  // [300] = subdued navy): a bright brand gradient glows too hard against the
  // dark page (2026-07 Home cohesion review). Light mode keeps the richer
  // gradient — the same deep-end trio resolves to pale tints there and would
  // break the white hero text.
  const heroFor = (X, isDarkMode) =>
    heroStyle === 'surface'
      ? {
          web: `linear-gradient(135deg, ${L.gray[800]} 0%, ${L.gray[900]} 55%, ${L.blue[900]} 100%)`,
          mobile: [L.gray[800], L.gray[900], L.blue[900]],
          accent: heroAccentCfg || L.blue[400],
        }
      : isDarkMode
        ? {
            web: `linear-gradient(135deg, ${X.blue[100]} 0%, ${X.blue[300]} 50%, ${X.blue[100]} 100%)`,
            mobile: [X.blue[100], X.blue[300], X.blue[100]],
            accent: heroAccentCfg || L.neutral.white,
          }
        : {
            web: `linear-gradient(135deg, ${X.blue[500]} 0%, ${X.blue[500]} 50%, ${X.blue[500]} 100%)`,
            mobile: [X.blue[300], X.blue[500], X.blue[300]],
            accent: heroAccentCfg || L.neutral.white,
          };
  const HERO = { light: heroFor(L, false), dark: heroFor(D, true) };

// ---------------------------------------------------------------------------
// Shared groups (one set of values for both apps)
// ---------------------------------------------------------------------------

// Dark-mode "neutral.gray" is the light ramp index-inverted (web convention),
// distinct from BASE_COLORS_DARK.gray which is its own hand-tuned dark ramp.
const INVERTED_GRAY_DARK = {
  50: L.gray[950],
  100: L.gray[900],
  200: L.gray[800],
  300: L.gray[700],
  400: L.gray[600],
  500: L.gray[500],
  600: L.gray[400],
  700: L.gray[300],
  800: L.gray[200],
  900: L.gray[100],
  950: L.gray[50],
  DEFAULT: L.gray[400]
};

const SEMANTIC = {
  light: {
    success: L.green[500],
    error: L.red[500],
    warning: L.yellow[500],
    info: L.blue[500],
  },
  dark: {
    success: L.green[400],
    error: L.red[400],
    warning: L.yellow[400],
    info: L.blue[400],
  },
};

// Both apps' key spellings are kept (web: absolute_*, mobile: *_absolute).
const NEUTRAL = {
  light: {
    white: L.neutral.white,
    black: L.neutral.black,
    gray: L.gray,
    absolute_black: L.neutral.black,
    absolute_white: L.neutral.white,
    white_absolute: L.neutral.white,
    black_absolute: L.neutral.black,
  },
  dark: {
    white: L.neutral.black,
    black: L.neutral.white,
    gray: INVERTED_GRAY_DARK,
    absolute_black: L.neutral.black,
    absolute_white: L.neutral.white,
    white_absolute: L.neutral.white,
    black_absolute: L.neutral.black,
  },
};

const BACKGROUNDS = {
  light: {
    primary: L.neutral.white,
    secondary: L.gray[50],
    tertiary: L.gray[100],
    // Screen surface BEHIND cards: one step off the card color so cards
    // separate by surface contrast (mirrors dark mode, where the page is
    // darker than the cards). Mobile dark pins this to the #121212 app
    // surface (see MOBILE_DARK_OVERRIDES).
    page: L.gray[50],
    card: rgbaString(L.neutral.white, 0.9),
    cardHover: L.neutral.white,
    modal: rgbaString(L.neutral.black, 0.4),
    overlay: rgbaString(L.neutral.white, 0.02),
    // Adaptive brand header/banner surface: brand-blue in light, neutral dark surface in
    // dark (the mobile THEME_DARK background pattern — rounded-bottom colored headers).
    header: L.blue[500],
    // Promoted mobile surface tokens (were mode-divergent MOBILE_ALIASES). Values preserved
    // verbatim from the aliases: footer/app/testReviewItem are pinned literals; tabbar/
    // tutorialDetails resolve to neutral white / dark gray[100] (so they rebrand as before).
    footer: '#f2f2f2',
    tabbar: L.neutral.white,
    app: '#F8FAFD',
    testReviewItem: '#EDF4FF',
    tutorialDetails: L.neutral.white,
    filterItem: '#F6FAFF',
    orange_gradient: `linear-gradient(90deg, ${L.orange[300]}, ${L.orange[500]})`,
    blue_gradient: `linear-gradient(90deg, ${L.blue[300]}, ${L.blue[500]})`,
    // Welcome-hero banner background — DERIVED from the active brand (see HERO above): web reads
    // hero_gradient (CSS string), mobile reads hero_gradient_colors (LinearGradient array). style
    // 'brand' reproduces the historical primary banner; style 'surface' (e.g. TrainingCamp) paints
    // the brand's dark slate + a deep-brand glow. No per-brand code override.
    hero_gradient: HERO.light.web,
    hero_gradient_colors: HERO.light.mobile,
  },
  dark: {
    primary: L.gray[900],
    secondary: L.gray[800],
    tertiary: L.gray[700],
    // See light block; web-dark default = the primary dark surface. Mobile
    // dark overrides this to the pinned #121212 app surface.
    page: L.gray[900],
    card: rgbaString(L.neutral.white, 0.05),
    cardHover: rgbaString(L.neutral.white, 0.08),
    // A modal scrim must DIM the content behind it (dark overlay), never wash it white.
    // The historical mobile MODAL_BACKGROUND_COLOR was rgba(255,255,255,0.2), which lightened
    // the page in dark mode (killing separation) — fixed to a dark scrim. (PR review #5)
    modal: rgbaString(L.neutral.black, 0.6),
    overlay: rgbaString(L.neutral.white, 0.02),
    // Dark side of the adaptive header: the neutral dark surface (matches mobile
    // THEME_DARK dark = D.gray[100]), so blue headers blend to a dark banner in dark mode.
    header: D.gray[100],
    // Promoted mobile surface tokens (see light block).
    footer: '#242424',
    tabbar: D.gray[100],
    app: L.neutral.black,
    testReviewItem: D.gray[100],
    tutorialDetails: L.neutral.black,
    filterItem: D.gray[100],
    orange_gradient: `linear-gradient(90deg, ${L.orange[300]}, ${L.orange[400]})`,
    blue_gradient: `linear-gradient(90deg, ${L.blue[300]}, ${L.blue[400]})`,
    // Dark variant: style 'brand' uses the dark primary scale (D) so the default-brand dark banner
    // is unchanged; style 'surface' is always the dark slate (L-derived, shared with light).
    hero_gradient: HERO.dark.web,
    hero_gradient_colors: HERO.dark.mobile,
  },
};

const BORDERS = {
  light: {
    primary: L.gray[200],
    secondary: L.gray[300],
    hover: L.gray[300],
    focus: L.blue[500],
    // Card outline tuned for HAIRLINE width (Home cards): light matches
    // `primary`'s color (the hairline width alone quiets it enough over the
    // gray page); dark stays fainter than `primary`.
    subtle: L.gray[200],
  },
  dark: {
    primary: rgbaString(L.neutral.white, 0.1),
    secondary: rgbaString(L.neutral.white, 0.15),
    hover: rgbaString(L.neutral.white, 0.2),
    focus: L.blue[400],
    subtle: rgbaString(L.neutral.white, 0.06),
  },
};

const TEXT = {
  light: {
    primary: L.gray[900],
    secondary: L.gray[600],
    tertiary: L.gray[500],
    inverse: L.neutral.white,
    brand: L.blue[600],
    // Accent color for the welcome-hero heading (the user's name) — DERIVED (see HERO above):
    // white on the default brand banner; the brand's on-dark tone on a 'surface'-style banner.
    hero_accent: HERO.light.accent,
  },
  dark: {
    primary: L.gray[50],
    // Reconciled (Step 3, "best value"): dark secondary/tertiary text adopt the LIGHTER,
    // higher-contrast legacy family (THEME_TEXT_BLACK_LIGHT #dddddd ≈ gray[100];
    // THEME_TEXT_GRAY #ADBACC ≈ gray[300]) for legibility. Brand-aware (L.gray), so
    // TrainingCamp gets slate-tinted grays; unified web+mobile (mobile override removed).
    secondary: L.gray[100],
    tertiary: L.gray[300],
    inverse: L.gray[900],
    brand: L.blue[400],
    hero_accent: HERO.dark.accent,
  },
};

const SHADOWS = {
  light: {
    sm: `0 1px 2px 0 ${rgbaString(L.gray[900], 0.05)}`,
    base: `0 1px 3px 0 ${rgbaString(L.gray[900], 0.1)}, 0 1px 2px 0 ${rgbaString(L.gray[900], 0.06)}`,
    md: `0 4px 6px -1px ${rgbaString(L.gray[900], 0.1)}, 0 2px 4px -1px ${rgbaString(L.gray[900], 0.06)}`,
    lg: `0 10px 15px -3px ${rgbaString(L.gray[900], 0.1)}, 0 4px 6px -2px ${rgbaString(L.gray[900], 0.05)}`,
    xl: `0 20px 25px -5px ${rgbaString(L.gray[900], 0.1)}, 0 10px 10px -5px ${rgbaString(L.gray[900], 0.04)}`,
    card: `0 4px 20px ${rgbaString(L.gray[900], 0.08)}`,
    cardHover: `0 8px 25px ${rgbaString(L.gray[900], 0.15)}`,
  },
  dark: {
    sm: `0 1px 2px 0 ${rgbaString(L.neutral.black, 0.3)}`,
    base: `0 1px 3px 0 ${rgbaString(L.neutral.black, 0.4)}, 0 1px 2px 0 ${rgbaString(L.neutral.black, 0.3)}`,
    md: `0 4px 6px -1px ${rgbaString(L.neutral.black, 0.4)}, 0 2px 4px -1px ${rgbaString(L.neutral.black, 0.3)}`,
    lg: `0 10px 15px -3px ${rgbaString(L.neutral.black, 0.4)}, 0 4px 6px -2px ${rgbaString(L.neutral.black, 0.3)}`,
    xl: `0 20px 25px -5px ${rgbaString(L.neutral.black, 0.5)}, 0 10px 10px -5px ${rgbaString(L.neutral.black, 0.4)}`,
    card: `0 4px 20px ${rgbaString(L.neutral.black, 0.3)}`,
    cardHover: `0 8px 25px ${rgbaString(L.neutral.black, 0.4)}`,
  },
};

// ---------------------------------------------------------------------------
// Shared blocks carried over from mobile (home menu, per-feature gradients)
// ---------------------------------------------------------------------------

// Every home-menu tile follows one formula; only the surface hue and the icon hue vary
// (Flashcards and StudyArticles split the pair). Each hue maps to a baked family (blue/purple/…);
// a brand retunes a tile by overriding the matching `categorical.*` hue in its configs/_shared
// theme.js (which re-bakes that family), not by restating tiles here.
const HOME_MENU_FEATURES = {
  StudyQuestions: { hue: 'blue', iconHue: 'blue' },
  PracticeTests: { hue: 'purple', iconHue: 'purple' },
  Flashcards: { hue: 'yellow', iconHue: 'gold' },
  StudyArticles: { hue: 'green', iconHue: 'emerald' },
  CreateCustomTest: { hue: 'orange', iconHue: 'orange' },
  Glossary: { hue: 'indigo', iconHue: 'indigo' },
  Acronyms: { hue: 'pink', iconHue: 'pink' },
};

const buildHomeMenu = (entry) => {
  const out = {};
  for (const [feature, { hue, iconHue }] of Object.entries(HOME_MENU_FEATURES)) {
    out[feature] = entry(hue, iconHue);
  }
  return out;
};

const HOME_MENU_COLORS = {
  light: buildHomeMenu((hue, iconHue) => ({
    background: L[hue][50],
    backgroundGradient: [L[hue][100], L[hue][50]],
    border: L[hue][100],
    bubble: L[hue][200],
    iconBackground: L[hue][100],
    iconGradient: [L[iconHue][300], L[iconHue][500]],
    iconColor: L.neutral.white,
  })),
  // Dark tiles share one neutral D.gray shell; only the icon colors vary.
  dark: buildHomeMenu((hue, iconHue) => ({
    background: D.gray[100],
    backgroundGradient: [D.gray[200], D.gray[100]],
    border: D.gray[100],
    bubble: D.gray[200],
    iconBackground: D[hue][100],
    iconGradient: [D[iconHue][500], D[iconHue][600]],
    iconColor: D.neutral.white,
  })),
};

// ---------------------------------------------------------------------------
// Shared blocks carried over from web (dashboard, buttons, hero, features…)
// ---------------------------------------------------------------------------

const DASHBOARD = {
  light: {
    card: {
      background: rgbaString(L.neutral.white, 0.9),
      backgroundHover: L.neutral.white,
      border: L.gray[200],
      borderHover: L.gray[300],
      shadow: `0 4px 20px ${rgbaString(L.gray[900], 0.08)}`,
      shadowHover: `0 8px 25px ${rgbaString(L.gray[900], 0.15)}`,
    },
    review: {
      itemBackground: L.gray[50],
      itemBackgroundHover: L.gray[100],
      badge: {
        all: {
          background: rgbaString(L.blue[500], 0.1),
          text: L.blue[700],
          border: rgbaString(L.blue[500], 0.3),
        },
        correct: {
          background: rgbaString(L.green[500], 0.1),
          text: L.green[700],
          border: rgbaString(L.green[500], 0.3),
        },
        incorrect: {
          background: rgbaString(L.red[500], 0.1),
          text: L.red[700],
          border: rgbaString(L.red[500], 0.3),
        },
        unanswered: {
          background: rgbaString(L.yellow[500], 0.1),
          text: L.yellow[700],
          border: rgbaString(L.yellow[500], 0.3),
        },
      },
    },
    domain: {
      cardBackground: L.gray[50],
      cardBackgroundHover: L.neutral.white,
      progressTrack: L.gray[200],
      progress: {
        critical: `linear-gradient(90deg, ${L.red[500]}, ${L.red[400]})`,
        warning: `linear-gradient(90deg, ${L.yellow[500]}, ${L.yellow[400]})`,
        success: `linear-gradient(90deg, ${L.green[500]}, ${L.green[400]})`,
      },
      border: {
        critical: L.red[500],
        warning: L.yellow[500],
        success: L.green[500],
      },
    },
    score: {
      background: L.gray[100],
      gradient: `linear-gradient(90deg, ${L.red[500]}, ${L.red[400]})`,
      dropShadow: rgbaString(L.red[500], 0.2),
    },
    button: {
      primary: {
        background: `linear-gradient(135deg, ${L.blue[500]} 0%, ${L.blue[600]} 100%)`,
        shadow: `0 4px 15px ${rgbaString(L.blue[500], 0.3)}`,
        shadowHover: `0 8px 25px ${rgbaString(L.blue[500], 0.4)}`,
      },
      secondary: {
        background: L.neutral.white,
        border: L.gray[200],
        borderHover: L.gray[300],
        text: L.gray[600],
        textHover: L.gray[900],
        shadow: `0 1px 2px 0 ${rgbaString(L.gray[900], 0.05)}`,
        shadowHover: `0 4px 6px -1px ${rgbaString(L.gray[900], 0.1)}`,
      },
    },
    status: {
      correct: L.green[500],
      incorrect: L.red[500],
      unanswered: L.yellow[500],
      primary: L.blue[600],
    },
  },
  dark: {
    card: {
      background: rgbaString(L.neutral.white, 0.05),
      backgroundHover: rgbaString(L.neutral.white, 0.08),
      border: rgbaString(L.neutral.white, 0.1),
      borderHover: rgbaString(L.neutral.white, 0.2),
      shadow: `0 4px 20px ${rgbaString(L.neutral.black, 0.3)}`,
      shadowHover: `0 8px 25px ${rgbaString(L.neutral.black, 0.4)}`,
    },
    review: {
      itemBackground: L.gray[800],
      itemBackgroundHover: L.gray[700],
      badge: {
        all: {
          background: rgbaString(L.blue[400], 0.1),
          text: L.blue[400],
          border: rgbaString(L.blue[400], 0.3),
        },
        correct: {
          background: rgbaString(L.green[400], 0.1),
          text: L.green[400],
          border: rgbaString(L.green[400], 0.3),
        },
        incorrect: {
          background: rgbaString(L.red[400], 0.1),
          text: L.red[400],
          border: rgbaString(L.red[400], 0.3),
        },
        unanswered: {
          background: rgbaString(L.yellow[400], 0.1),
          text: L.yellow[400],
          border: rgbaString(L.yellow[400], 0.3),
        },
      },
    },
    domain: {
      cardBackground: L.gray[800],
      cardBackgroundHover: rgbaString(L.neutral.white, 0.08),
      progressTrack: rgbaString(L.neutral.white, 0.1),
      progress: {
        critical: `linear-gradient(90deg, ${L.red[400]}, ${L.red[300]})`,
        warning: `linear-gradient(90deg, ${L.yellow[400]}, ${L.yellow[300]})`,
        success: `linear-gradient(90deg, ${L.green[400]}, ${L.green[300]})`,
      },
      border: {
        critical: L.red[400],
        warning: L.yellow[400],
        success: L.green[400],
      },
    },
    score: {
      background: rgbaString(L.neutral.white, 0.1),
      gradient: `linear-gradient(90deg, ${L.red[400]}, ${L.red[300]})`,
      dropShadow: rgbaString(L.red[400], 0.3),
    },
    button: {
      primary: {
        background: `linear-gradient(135deg, ${L.blue[400]} 0%, ${L.blue[500]} 100%)`,
        shadow: `0 4px 15px ${rgbaString(L.blue[400], 0.3)}`,
        shadowHover: `0 8px 25px ${rgbaString(L.blue[400], 0.4)}`,
      },
      secondary: {
        background: rgbaString(L.neutral.white, 0.05),
        border: rgbaString(L.neutral.white, 0.1),
        borderHover: rgbaString(L.neutral.white, 0.15),
        text: L.gray[300],
        textHover: L.gray[50],
        shadow: `0 1px 2px 0 ${rgbaString(L.neutral.black, 0.3)}`,
        shadowHover: `0 4px 6px -1px ${rgbaString(L.neutral.black, 0.4)}`,
      },
    },
    status: {
      correct: L.green[400],
      incorrect: L.red[400],
      unanswered: L.yellow[400],
      primary: L.blue[400],
    },
  },
};

// Non-color metrics carried with the web theme for back-compat.
const BUTTON_SIZES = {
  sm: {
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    borderRadius: '0.5rem',
  },
  md: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    borderRadius: '0.75rem',
  },
  lg: {
    padding: '1rem 2rem',
    fontSize: '1.125rem',
    borderRadius: '0.75rem',
  }
};

const BUTTONS = {
  light: {
    primary: {
      background: `linear-gradient(135deg, ${L.blue[500]} 0%, ${L.blue[600]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${L.blue[600]} 0%, ${L.blue[700]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${L.blue[700]} 0%, ${L.blue[800]} 100%)`,
      backgroundDisabled: L.gray[300],
      text: L.neutral.white,
      textDisabled: L.gray[500],
      border: L.blue[600],
      borderHover: L.blue[700],
      shadow: `0 4px 12px ${rgbaString(L.blue[600], 0.3)}`,
      shadowHover: `0 6px 20px ${rgbaString(L.blue[600], 0.4)}`,
    },
    secondary: {
      background: `linear-gradient(135deg, ${L.orange[400]} 0%, ${L.orange[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${L.orange[500]} 0%, ${L.orange[600]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${L.orange[600]} 0%, ${L.orange[700]} 100%)`,
      backgroundDisabled: L.gray[300],
      text: L.neutral.white,
      textDisabled: L.gray[500],
      border: L.orange[500],
      borderHover: L.orange[600],
      shadow: `0 4px 12px ${rgbaString(L.orange[500], 0.3)}`,
      shadowHover: `0 6px 20px ${rgbaString(L.orange[500], 0.4)}`,
    },
    outlinePrimary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${L.blue[50]} 0%, ${L.blue[100]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${L.blue[100]} 0%, ${L.blue[200]} 100%)`,
      backgroundDisabled: 'transparent',
      text: L.blue[600],
      textHover: L.blue[700],
      textDisabled: L.gray[400],
      border: L.blue[300],
      borderHover: L.blue[400],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(L.blue[600], 0.1)}`,
    },
    outlineSecondary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${L.orange[50]} 0%, ${L.orange[100]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${L.orange[100]} 0%, ${L.orange[200]} 100%)`,
      backgroundDisabled: 'transparent',
      text: L.orange[600],
      textHover: L.orange[700],
      textDisabled: L.gray[400],
      border: L.orange[300],
      borderHover: L.orange[400],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(L.orange[500], 0.1)}`,
    },
    sizes: BUTTON_SIZES,
  },
  dark: {
    primary: {
      background: `linear-gradient(135deg, ${D.blue[400]} 0%, ${D.blue[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${D.blue[300]} 0%, ${D.blue[400]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${D.blue[500]} 0%, ${D.blue[600]} 100%)`,
      backgroundDisabled: D.gray[700],
      text: D.neutral.white,
      textDisabled: D.gray[500],
      border: D.blue[500],
      borderHover: D.blue[400],
      shadow: `0 4px 12px ${rgbaString(L.neutral.black, 0.4)}`,
      shadowHover: `0 6px 20px ${rgbaString(D.blue[500], 0.5)}`,
    },
    secondary: {
      background: `linear-gradient(135deg, ${D.orange[400]} 0%, ${D.orange[500]} 100%)`,
      backgroundHover: `linear-gradient(135deg, ${D.orange[300]} 0%, ${D.orange[400]} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${D.orange[500]} 0%, ${D.orange[600]} 100%)`,
      backgroundDisabled: D.gray[700],
      text: D.neutral.white,
      textDisabled: D.gray[500],
      border: D.orange[500],
      borderHover: D.orange[400],
      shadow: `0 4px 12px ${rgbaString(L.neutral.black, 0.4)}`,
      shadowHover: `0 6px 20px ${rgbaString(D.orange[500], 0.5)}`,
    },
    outlinePrimary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${rgbaString(D.blue[500], 0.1)} 0%, ${rgbaString(D.blue[400], 0.15)} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${rgbaString(D.blue[500], 0.2)} 0%, ${rgbaString(D.blue[400], 0.25)} 100%)`,
      backgroundDisabled: 'transparent',
      text: D.blue[400],
      textHover: D.blue[300],
      textDisabled: D.gray[600],
      border: D.blue[600],
      borderHover: D.blue[500],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(D.blue[500], 0.2)}`,
    },
    outlineSecondary: {
      background: 'transparent',
      backgroundHover: `linear-gradient(135deg, ${rgbaString(D.orange[500], 0.1)} 0%, ${rgbaString(D.orange[400], 0.15)} 100%)`,
      backgroundActive: `linear-gradient(135deg, ${rgbaString(D.orange[500], 0.2)} 0%, ${rgbaString(D.orange[400], 0.25)} 100%)`,
      backgroundDisabled: 'transparent',
      text: D.orange[400],
      textHover: D.orange[300],
      textDisabled: D.gray[600],
      border: D.orange[600],
      borderHover: D.orange[500],
      shadow: 'none',
      shadowHover: `0 4px 12px ${rgbaString(D.orange[500], 0.2)}`,
    },
    sizes: BUTTON_SIZES,
  },
};

// ---------------------------------------------------------------------------
// Per-mode shared theme (everything except the per-platform legacy aliases)
// ---------------------------------------------------------------------------

const SHARED_LIGHT = {
  primary: L,
  semantic: SEMANTIC.light,
  neutral: NEUTRAL.light,
  backgrounds: BACKGROUNDS.light,
  borders: BORDERS.light,
  text: TEXT.light,
  shadows: SHADOWS.light,

  electric: ELECTRIC_COLORS,
  home_menu_colors: HOME_MENU_COLORS.light,
  menu_chips: MENU_CHIP_COLORS,

  white: NEUTRAL.light.white,
  black: NEUTRAL.light.black,
  gray: NEUTRAL.light.gray,
  green: L.green,
  red: L.red,
  yellow: L.yellow,

  hero_background: {
    0: L.blue[50],
    15: L.blue[100],
    30: lighten(L.blue[200], 5),
    45: L.blue[200],
    60: lighten(L.blue[300], 5),
    75: L.blue[300],
    90: lighten(L.blue[400], 5),
    100: L.blue[400]
  },

  navbar_test: {
    background: L.blue[500],
    text: L.neutral.black,
    progress_bar: L.blue[200],
    progress_bar_fill: L.blue[400],
    dot_color: L.neutral.black,
    end_button: L.red[500],
    end_button_hover: L.red[200],
    end_button_text: L.neutral.black,
    arrow_background: L.orange[500],
  },

  buttons: BUTTONS.light,

  background: {
    main: L.neutral.white,
    primary: L.neutral.white,
    secondary: L.gray[50],
    tertiary: L.gray[100],
    orange: rgbaString(L.orange[500], 0.15),
    blue: L.blue[50],
    input: L.gray[50],
    card: L.gray[50],
    separator: L.gray[200],
    modal: BACKGROUNDS.light.modal,
    premium: lighten(L.orange[100], 10),
    orange_gradient: BACKGROUNDS.light.orange_gradient,
    blue_gradient: BACKGROUNDS.light.blue_gradient,
  },

  ui: {
    primary: L.blue[500],
    secondary: L.orange[500],
    success: SEMANTIC.light.success,
    warning: SEMANTIC.light.warning,
    error: SEMANTIC.light.error,
    border: BORDERS.light.primary,
  },

  features: {
    study: {
      background: L.blue[50],
      icon: L.blue[500],
    },
    practice: {
      background: L.yellow[50],
      icon: L.yellow[600],
    },
    flashcard: {
      background: L.green[50],
      icon: L.green[600],
    },
    custom: {
      background: L.red[50],
      icon: L.red[600],
    },
  },

  dashboard: DASHBOARD.light,
};

const SHARED_DARK = {
  primary: D,
  semantic: SEMANTIC.dark,
  neutral: NEUTRAL.dark,
  backgrounds: BACKGROUNDS.dark,
  borders: BORDERS.dark,
  text: TEXT.dark,
  shadows: SHADOWS.dark,

  electric: ELECTRIC_COLORS,
  home_menu_colors: HOME_MENU_COLORS.dark,
  menu_chips: MENU_CHIP_COLORS,

  white: NEUTRAL.dark.white,
  black: NEUTRAL.dark.black,
  gray: NEUTRAL.dark.gray,
  green: D.green,
  red: D.red,
  yellow: D.yellow,

  hero_background: {
    0: D.blue[200],
    15: D.blue[300],
    30: D.blue[400],
    45: D.blue[500],
    60: D.blue[600],
    75: D.blue[700],
    90: D.blue[800],
    100: D.blue[900]
  },

  navbar_test: {
    background: L.blue[500],
    text: NEUTRAL.dark.black,
    progress_bar: D.blue[200],
    progress_bar_fill: D.blue[400],
    dot_color: NEUTRAL.dark.black,
    end_button: D.red[400],
    end_button_hover: D.red[700],
    end_button_text: NEUTRAL.dark.black,
    arrow_background: L.orange[500],
  },

  buttons: BUTTONS.dark,

  background: {
    main: L.neutral.black,
    primary: L.neutral.black,
    secondary: L.gray[900],
    tertiary: L.gray[800],
    orange: rgbaString(L.orange[500], 0.25),
    blue: rgbaString(L.blue[500], 0.4),
    input: BACKGROUNDS.dark.secondary,
    card: L.gray[900],
    separator: BACKGROUNDS.dark.tertiary,
    modal: BACKGROUNDS.dark.modal,
    premium: darken(D.orange[100], 10),
    orange_gradient: BACKGROUNDS.dark.orange_gradient,
    blue_gradient: BACKGROUNDS.dark.blue_gradient,
  },

  ui: {
    primary: L.blue[500],
    secondary: L.orange[500],
    success: SEMANTIC.dark.success,
    warning: SEMANTIC.dark.warning,
    error: SEMANTIC.dark.error,
    border: BORDERS.dark.primary,
  },

  features: {
    study: {
      background: BACKGROUNDS.dark.secondary,
      icon: D.blue[400],
    },
    practice: {
      background: BACKGROUNDS.dark.secondary,
      icon: D.yellow[400],
    },
    flashcard: {
      background: BACKGROUNDS.dark.secondary,
      icon: D.green[400],
    },
    custom: {
      background: BACKGROUNDS.dark.secondary,
      icon: D.red[400],
    },
  },

  dashboard: DASHBOARD.dark,
};


// ---------------------------------------------------------------------------
// Assembled themes + accessor
// ---------------------------------------------------------------------------

// Transitional mobile-only dark overrides. These are values mobile shipped
// DELIBERATELY (unlike the inverted-ramp bugs the shared groups fix):
//  - backgrounds.card: opaque #1E1F25 — RN iOS shadow* / Android elevation
//    misrender on translucent backgrounds, and mobile cards set both together.
//  - neutral.white: #121212 soft black (Material dark surface), used as the
//    screen/card background tone across Home, Login, Register, articles, etc.
//  - text.secondary/tertiary: mobile's own dark-ramp grays — web's are one
//    stop brighter, which is a contrast retune, not a bug fix.
// Everything else in the shared groups is identical to web. Each override is
// pinned in __tests__/theme.test.js; retiring them is a deliberate restyle.
// Mobile dark overrides. `neutral.white` is a genuine brand-independent constant (the
// mobile soft-black app surface) → pinned literal. `backgrounds.card` is opaque (RN
// shadow/elevation breaks on translucent bg) but its COLOR stays BRAND-AWARE via D.gray,
// so it must NOT be hardcoded (a literal would clobber TrainingCamp's slate surface).
// The text-gray override was REMOVED in Step 3: dark text is now unified with web on the
// reconciled lighter TEXT.dark values (see TEXT above). See docs/THEME_SYSTEM_PHASE1.md.
const MOBILE_DARK_OVERRIDES = {
  neutral: { ...NEUTRAL.dark, white: '#121212' },
  backgrounds: { ...BACKGROUNDS.dark, card: D.gray[100], page: '#121212' },
};

// Mobile light override, symmetric with the dark card pin above. The shared light card is
// `rgba(255,255,255,0.9)` — a web "glass" surface. On mobile that translucency is wrong for
// card/modal surfaces: (1) RN mis-renders shadow*/elevation over translucent fills (the same
// reason the dark card is pinned opaque), and (2) modal cards sit over a scrim, so a 0.9-alpha
// body lets the dimmed page ghost through and the card loses separation. Pin the mobile light
// card to the honest OPAQUE form of the value it approximates (`L.neutral.white` = #ffffff).
// Brand-independent in light (all brands' light card surface is white); pinned in
// __tests__/theme.test.js.
const MOBILE_LIGHT_OVERRIDES = {
  backgrounds: { ...BACKGROUNDS.light, card: L.neutral.white },
};

  const LIGHT_THEME_WEB = { ...SHARED_LIGHT };
  const DARK_THEME_WEB = { ...SHARED_DARK };
  const LIGHT_THEME_MOBILE = { ...SHARED_LIGHT, ...MOBILE_LIGHT_OVERRIDES };
  const DARK_THEME_MOBILE = { ...SHARED_DARK, ...MOBILE_DARK_OVERRIDES };

  return { LIGHT_THEME_WEB, DARK_THEME_WEB, LIGHT_THEME_MOBILE, DARK_THEME_MOBILE };
}

// ---------------------------------------------------------------------------
// Active-brand singletons + accessor
// ---------------------------------------------------------------------------

// One brand per build: the active brand is whatever was baked into
// generated/activeBrandScales.js (by `switch <brand>`), so the themes are built once.
const THEMES = buildThemes();

export const LIGHT_THEME_WEB = THEMES.LIGHT_THEME_WEB;
export const DARK_THEME_WEB = THEMES.DARK_THEME_WEB;
export const LIGHT_THEME_MOBILE = THEMES.LIGHT_THEME_MOBILE;
export const DARK_THEME_MOBILE = THEMES.DARK_THEME_MOBILE;

/**
 * Drop-in replacement for both apps' theme accessors.
 *
 * web ThemeProvider:    getColorTheme(mode, 'web')     // platform defaults to 'web'
 * mobile ThemeProvider: getColorTheme(mode, 'mobile')
 *
 * `mode` follows the existing convention in both apps: 'light' selects the light theme, anything
 * else selects dark. The third `brand` argument is accepted for call-site back-compat but is now a
 * no-op — the active brand is fixed at build time (baked into generated/activeBrandScales.js by the
 * switch script), so there is a single brand's theme to serve.
 */
export function getColorTheme(mode, platform = 'web', _brand = 'default') {
  if (platform === 'mobile') {
    return mode === 'light' ? LIGHT_THEME_MOBILE : DARK_THEME_MOBILE;
  }
  return mode === 'light' ? LIGHT_THEME_WEB : DARK_THEME_WEB;
}
